import React from "react";
import FileList from "./FilesView/FileList";
import PreviewMapComponent from "./Map/PreviewMapComponent";
import ErrorBoundary from "./ErrorBoundary";
import { FaRegLightbulb, FaExternalLinkAlt } from "react-icons/fa";
import SideDrawer from "./Download/SideDrawer";
import AISummaryContent from "./AISummaryContent";

const StaticTooltip = ({ content, children }) => (
  <div className="static-tooltip-container">
    {children}
    <span className="static-tooltip-text">{content}</span>
  </div>
);

/**
 * The popup's 3-column details view (info/contact tabs, spatial preview,
 * files/downloads/AI-summary tabs).
 */
function PopupDetailsView({
  details,
  spatialExtent,
  infoTab,
  setInfoTab,
  activeTab,
  setActiveTab,
  archiveFiles,
  summaryFiles,
  memoizedFilesView,
  loadingBaselineModel,
  handleBaselineModelClick,
  parseBaselineModels,
  renderField,
  renderFieldForParameters,
  getArchiveFileString,
  renderArchiveFile,
  isDrawerOpen,
  setIsDrawerOpen,
  fileName,
  fileUrl,
  sizeInGB,
  instructionUrl,
  resolveInstructionUrl,
  handleBrowserDownload,
  onExpandSummary,
}) {
  return (
    <>
      <div className="popup-section left-section">
        <div
          style={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div className="tabs">
            {/* fixed this accessibility issue,make the clickable div element keyboard-accessible by:
            Adding a tabIndex prop to make it focusable
            Adding an onKeyDown handler for keyboard interaction
            Using the semantically appropriate ARIA role
            Adding appropriate ARIA labels  */}
            <div
              className={`tab ${infoTab === "general" ? "active" : ""}`}
              onClick={() => setInfoTab("general")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setInfoTab("general");
                }
              }}
              role="tab"
              tabIndex={0}
              aria-selected={infoTab === "general"}
              aria-controls="general-tab-content"
            >
              General Information
            </div>
            {/* fixed this accessibility issue,make the clickable div element keyboard-accessible by:
            Adding a tabIndex prop to make it focusable
            Adding an onKeyDown handler for keyboard interaction
            Using the semantically appropriate ARIA role
            Adding appropriate ARIA labels  */}
            <div
              className={`tab ${infoTab === "contact" ? "active" : ""}`}
              onClick={() => setInfoTab("contact")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setInfoTab("contact");
                }
              }}
              role="tab"
              tabIndex={0}
              aria-selected={infoTab === "contact"}
              aria-controls="contact-tab-content"
            >
              Contact Information
            </div>
          </div>
          <div className="tab-content">
            {infoTab === "general" && (
              <div
                id="general-tab-content"
                role="tabpanel"
                aria-labelledby="general-tab"
                hidden={infoTab !== "general"}
              >
                {(details.simulation_description ||
                  details.simulation_temporal_extent ||
                  details.simulation_temporal_resolution) &&
                  renderField("Model Name", details.model_name)}

                {details.simulation_description &&
                  renderField(
                    "Simulation Description",
                    details.simulation_description,
                  )}
                {details.simulation_temporal_extent &&
                  renderField(
                    "Simulation Temporal Extent",
                    details.simulation_temporal_extent,
                  )}
                {details.simulation_temporal_resolution &&
                  renderField(
                    "Simulation Temporal Resolution",
                    details.simulation_temporal_resolution,
                  )}
                {details?.model_category === "Alternative Analysis Model" && (
                  <>
                    <h3>Baseline Model:</h3>
                    <p style={{ wordBreak: "break-word" }}>
                      {(() => {
                        const baselineModels = parseBaselineModels(
                          details.baseline_model,
                        );
                        if (baselineModels.length === 0) {
                          return (
                            <span className="black-text">
                              N/A<span className="red-asterisk">*</span>
                            </span>
                          );
                        }
                        return baselineModels.map((modelName, index) => {
                          const isLoading = loadingBaselineModel === modelName;

                          // check if it's External model
                          const isExternalModel =
                            modelName?.toLowerCase() === "external model";

                          return (
                            <React.Fragment key={index}>
                              {isExternalModel ? (
                                // plain text version for external model
                                <span
                                  style={{
                                    color: "var(--color-text, #333)", // Normal text color, no pointer/underline
                                  }}
                                >
                                  {modelName}
                                </span>
                              ) : (
                                <span
                                  onClick={() => {
                                    if (!isLoading) {
                                      handleBaselineModelClick(modelName);
                                    }
                                  }}
                                  style={{
                                    textDecoration: "underline",
                                    cursor: isLoading ? "wait" : "pointer",
                                    transition: "color 0.2s ease",
                                    color: isLoading
                                      ? "var(--color-text-muted, #999)"
                                      : "var(--color-primary, #6E8CA0)",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isLoading) {
                                      e.target.style.color =
                                        "var(--color-accent, #87BCBF)";
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isLoading) {
                                      e.target.style.color =
                                        "var(--color-primary, #6E8CA0)";
                                    }
                                  }}
                                >
                                  {modelName}
                                </span>
                              )}
                              {index < baselineModels.length - 1 && ", "}
                            </React.Fragment>
                          );
                        });
                      })()}
                    </p>
                  </>
                )}

                {renderField("HUC", details.huc_code)}
                {renderField("County", details.county_name)}
                {renderField("City", details.city_name)}
                {renderField("Model Description", details.description)}
                {renderField("Model Purpose", details.model_purpose)}
                {renderField("Update Frequency", details.update_frequency)}
                {renderField("Model Type", details.model_type)}
                {renderField(
                  "Model Software Name",
                  details.model_software_name,
                )}
                {renderField(
                  "Model Software version",
                  details.model_software_version,
                )}
                {renderField("Time Period", details.time_period)}
                {renderField(
                  "Model Last Updated",
                  details.model_last_updated_dt,
                )}
              </div>
            )}
            {infoTab === "contact" && (
              <div
                id="contact-tab-content"
                role="tabpanel"
                aria-labelledby="contact-tab"
                hidden={infoTab !== "contact"}
              >
                {renderField(
                  "Contact Affiliation",
                  details.contact_affiliation,
                )}
                {renderField("Contact Department", details.contact_department)}
                {renderField("Contact Name", details.contact_name)}
                {renderField("Contact Email", details.contact_email)}
                {renderField(
                  "Contact Phone number",
                  details.contact_phone_number,
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="popup-section middle-section">
        {spatialExtent === null ? (
          <div className="loading-container" role="status" aria-live="polite">
            <p className="loading-style">Loading Spatial Extents...</p>
          </div>
        ) : spatialExtent.length === 0 ? (
          <div className="loading-container" role="status">
            <p className="loading-style">No spatial extent available</p>
          </div>
        ) : (
          <PreviewMapComponent data={spatialExtent} details={details} />
        )}
      </div>
      <div className="popup-section right-section">
        {activeTab === "summary" && (
          <button
            type="button"
            className="ai-summary-expand-button"
            onClick={onExpandSummary}
            aria-label="Expand AI Summary to full width"
          >
            <FaExternalLinkAlt aria-hidden="true" />
          </button>
        )}
        <div
          style={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Tabs */}
          <div className="tabs">
            <StaticTooltip content="Searchable listing of files within the model package.">
              {/* fixed this accessibility issue,make the clickable div element keyboard-accessible by:
              Adding a tabIndex prop to make it focusable
              Adding an onKeyDown handler for keyboard interaction
              Using the semantically appropriate ARIA role
              Adding appropriate ARIA labels  */}
              <div
                className={`tab ${activeTab === "tree" ? "active" : ""}`}
                onClick={() => setActiveTab("tree")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setActiveTab("tree");
                  }
                }}
                role="tab"
                tabIndex={0}
                aria-selected={activeTab === "tree"}
                aria-controls="tree-tab-content"
              >
                Model Files Listing
              </div>
            </StaticTooltip>
            <StaticTooltip content="Download model package. Additional files are available for some of the models.">
              <div
                className={`tab ${activeTab === "downloads" ? "active" : ""}`}
                onClick={() => setActiveTab("downloads")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setActiveTab("downloads");
                  }
                }}
                role="tab"
                tabIndex={0}
                aria-selected={activeTab === "downloads"}
                aria-controls="downloads-tab-content"
              >
                Available Files for Download
              </div>
            </StaticTooltip>
            <StaticTooltip content="AI-generated overview of this model's data, outputs, and insights.">
              <div
                className={`tab ai-summary-tab ${activeTab === "summary" ? "active" : ""}`}
                onClick={() => setActiveTab("summary")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setActiveTab("summary");
                  }
                }}
                role="tab"
                tabIndex={0}
                aria-selected={activeTab === "summary"}
                aria-controls="summary-tab-content"
              >
                <FaRegLightbulb className="ai-tab-icon" aria-hidden="true" />
                AI Summary
              </div>
            </StaticTooltip>
          </div>

          {/* Tab Content */}
          <div className="tab-content" style={{ paddingBottom: 20 }}>
            {activeTab === "tree" && (
              <div
                id="tree-tab-content"
                role="tabpanel"
                aria-labelledby="tree-tab"
                hidden={activeTab !== "tree"}
              >
                <div>
                  <ErrorBoundary>
                    {details?.files ? (
                      memoizedFilesView
                    ) : (
                      <div className="loading-container">
                        <p className="loading-style">Loading...</p>
                      </div>
                    )}
                  </ErrorBoundary>

                  {details.input_files && details.input_files.length > 0 ? (
                    <div>
                      <br />
                      <h3>Input Files</h3>
                      <FileList files={details.input_files} />
                    </div>
                  ) : (
                    <div></div>
                  )}

                  {details.output_files && details.output_files.length > 0 ? (
                    <div>
                      <br />
                      <h3>Output Files</h3>
                      <FileList files={details.output_files} />
                    </div>
                  ) : (
                    <div></div>
                  )}

                  {details.parameters && details.parameters.length > 0 ? (
                    <div>
                      <br />
                      <h3>Parameters</h3>
                      <br></br>
                      {details.parameters.map((param) => {
                        const [key, value] = Object.entries(param)[0];
                        return renderFieldForParameters(key, value);
                      })}
                    </div>
                  ) : (
                    <div>
                      <br></br>
                    </div>
                  )}
                </div>
              </div>
            )}
            {activeTab === "downloads" && (
              <>
                <div
                  id="downloads-tab-content"
                  role="tabpanel"
                  aria-labelledby="downloads-tab"
                  hidden={activeTab !== "downloads"}
                >
                  {details.source_application &&
                  details.source_application.toLowerCase() === "rbfs" ? (
                    <>
                      <h3>
                        <u>Complete Model Package</u>
                      </h3>
                      {archiveFiles.length > 0 &&
                        renderArchiveFile(archiveFiles[0])}
                    </>
                  ) : (
                    <>
                      <h3>
                        <u>Complete Model Package</u>
                      </h3>
                      {archiveFiles.map((archive) => {
                        const archiveFile =
                          getArchiveFileString(archive).toLowerCase();
                        if (archiveFile && !archiveFile.includes("results")) {
                          return renderArchiveFile(archive);
                        }
                        return null;
                      })}
                    </>
                  )}

                  {archiveFiles.some((f) => {
                    const archiveFile = getArchiveFileString(f).toLowerCase();
                    return (
                      archiveFile.includes("results") &&
                      archiveFile.includes(".zip")
                    );
                  }) &&
                    details.source_application &&
                    details.source_application.toLowerCase() === "rbfs" && (
                      <>
                        <br />
                        <h3>
                          <u>Model Results</u>
                        </h3>
                        {archiveFiles.map((archive) => {
                          const archiveFile =
                            getArchiveFileString(archive).toLowerCase();
                          if (
                            archiveFile.includes("results") &&
                            archiveFile.includes(".zip")
                          ) {
                            return renderArchiveFile(archive);
                          }
                        })}
                      </>
                    )}

                  {archiveFiles.some((f) => {
                    const archiveFile = getArchiveFileString(f).toLowerCase();
                    return (
                      archiveFile.includes(".pdf") ||
                      archiveFile.includes(".pptx")
                    );
                  }) &&
                    details.source_application &&
                    details.source_application.toLowerCase() === "rbfs" && (
                      <>
                        <br />
                        <h3>
                          <u>Reports</u>
                        </h3>
                        {archiveFiles.map((archive) => {
                          const fileName =
                            getArchiveFileString(archive).toLowerCase();
                          if (
                            fileName.includes(".pdf") ||
                            fileName.includes(".pptx")
                          ) {
                            return renderArchiveFile(archive);
                          }
                        })}
                      </>
                    )}

                  {archiveFiles.some((file) => {
                    const name = getArchiveFileString(file).toLowerCase();
                    return (
                      name.includes(".zip") &&
                      !name.includes("final.zip") &&
                      !name.includes("results")
                    );
                  }) &&
                    details.source_application &&
                    details.source_application.toLowerCase() === "rbfs" && (
                      <>
                        <br />
                        <h3>
                          <u>Spatial Data</u>
                        </h3>
                        {archiveFiles.slice(1).map((file) => {
                          const name = getArchiveFileString(file).toLowerCase();
                          if (
                            name.includes(".zip") &&
                            !name.includes("final.zip") &&
                            !name.includes("results")
                          ) {
                            return renderArchiveFile(file);
                          }
                          return null;
                        })}
                      </>
                    )}

                  <SideDrawer
                    isOpen={isDrawerOpen}
                    onClose={() => setIsDrawerOpen(false)}
                    fileName={fileName}
                    sizeInGB={sizeInGB}
                    fileUrl={fileUrl}
                    instructionUrl={instructionUrl}
                    resolveInstructionUrl={resolveInstructionUrl}
                    handleBrowserDownload={() =>
                      handleBrowserDownload(fileUrl, fileName)
                    }
                  />
                </div>
                <br />
                <br />
              </>
            )}
            {activeTab === "summary" && (
              <div
                id="summary-tab-content"
                role="tabpanel"
                aria-labelledby="summary-tab"
              >
                <AISummaryContent
                  guuid={details.tdis_identifier}
                  folder={summaryFiles}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default PopupDetailsView;
